import { showErrorToast, showInfoToast } from '$tablet-app/src/app/toasts';
import { useCurrentUserID } from '$tablet-app/src/app/users';
import {
  CalendarItemUnion,
  useGetCalendarItem,
  useUpdateCalendarItem,
} from '@akin-home/calendar/combined';
import React, { useCallback, useEffect } from 'react';

import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import {
  useSaveCommand,
  useUpdateScreenChatContext,
} from '$tablet-app/src/app/chat/hooks';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { JSONText } from '$tablet-app/src/app/developer/components/JSONText';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator/RootStackNavigator';
import { afterUpdateCalendarItem } from '../../functions/afterUpdateCalendarItem';
import { useUpdateCalendarItemFormListener } from '../../hooks';
import { validateCalendarItemFormSubmission } from '../CalendarItemForm';
import CalendarItemForm from '../CalendarItemForm/CalendarItemForm';
import { useCalendarItemForm } from '../CalendarItemForm/useCalendarItemForm';

const context = {
  canSave: true,

  fields: {
    title: {
      type: 'text',
    },
    emoji: {
      type: 'emoji',
    },
    start_date: {
      type: 'date_text',
    },
    start_time: {
      type: 'hh:mm or hh:mm:ss',
    },
    end_date: {
      type: 'date_text',
    },
    end_time: {
      type: 'hh:mm or hh:mm:ss',
    },
    notes: {
      type: 'text',
    },
    color: {
      type: 'color',
    },
  },
};

/** Screen Component for creating calendar items (events, alerts and alarms) */
const EditCalendarItemScreen = (
  props: RootStackScreenProps<'EditCalendarItem'>
) => {
  const {
    navigation: { goBack },
    route: {
      params: { id, type, focused_field },
    },
  } = props;

  const user_id = useCurrentUserID();
  const update = useUpdateCalendarItem();

  useUpdateScreenChatContext(context);

  const { item } = useGetCalendarItem<CalendarItemUnion>({
    id,
    type,
  });

  const [state, dispatch] = useCalendarItemForm({
    item,
    mode: 'edit',
  });

  useUpdateCalendarItemFormListener(
    useCallback(
      (item) => {
        dispatch({ type: 'update_item', payload: { item } });
      },
      [dispatch]
    )
  );

  const onSave = useSaveCommand(
    useCallback(() => {
      if (!state.item) return;

      const check = validateCalendarItemFormSubmission({
        mode: 'edit',
        item: state.item,
      });

      if ('error' in check) {
        showErrorToast({
          text1: 'Submission Error',
          text2: check.error,
        });

        return;
      }

      showInfoToast({
        text1: `Updating ${state.item.type}...`,
      });

      update(state.item).then(() => {
        state.item && afterUpdateCalendarItem({ item: state.item });
      });

      goBack();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.item, user_id])
  );

  useEffect(() => {
    if (!state.item && item) {
      dispatch({ type: 'set_item', payload: { item } });

      sendBackgroundMessage(intent_labels.open_edit_calendar_item_screen, {
        context: {
          item: {
            id: item.id,
            type: item.type,
            title: item.title,
            start: item.start,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  return (
    <>
      <CalendarItemForm
        state={state}
        dispatch={dispatch}
        onPressBack={goBack}
        onPressSave={onSave}
        focusedField={focused_field}
      />

      <DeveloperScreenInfo
        context={<JSONText>{context}</JSONText>}
        // parameters={<JSONText>{parameters}</JSONText>}
      />
    </>
  );
};

export default EditCalendarItemScreen;
